import React, { useEffect, useState } from 'react';
import { MenuInfo } from 'rc-menu/lib/interface';
import { MENU_ITEMS } from './constants/MenuItems';

import BenefitContext from './contexts/BenefitContext';
import BenefitActionSection from './components/BenefitActionSection';
import BenefitTitle from './components/BenefitTitle';
import BenefitsBody from './components/BenefitsBody';
import BenefitFilters from './components/BenefitFilters';
import BenefitMenu from './components/BenefitMenu';
import { ImportantNotesButton } from './sections/outpatients/components/ImportantNotesButton/ImportantNotesButton';

import { SelectionName, SelectionPeriod, SWR_KEY, OUTPATIENT_TYPES } from 'pages/benefits/types/benefitTypes';
import { usePolicyInfo, useHasSurgeryClassification, useBenefitClaimType } from 'all-in-one-shared-api';
import { useKeycloakReady } from 'hooks/useKeycloakReady';
import { Skeleton } from 'antd';
import { getCurrentPolicy } from './utils/utils';
import { getFamilyRelationshipByCode } from 'utils/benefits';
import styled from 'styled-components';

const OUTPATIENT_OPTIONS = [{ id: OUTPATIENT_TYPES.COVERAGE }, { id: OUTPATIENT_TYPES.USAGE }];

const BenefitNotice = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
`;

const BenefitsPage = () => {
  const [selectedKey, setSelectedKey] = useState<string>(MENU_ITEMS[0].id);
  const [selectedFilterName, setSelectedFilterName] = useState<Partial<SelectionName>>({});
  const [selectedFilterPeriod, setSelectedFilterPeriod] = useState<Partial<SelectionPeriod>>({});
  const [selectedOutpatientTab, setSelectedOutpatientTab] = useState<OUTPATIENT_TYPES>(OUTPATIENT_OPTIONS[0].id);

  const { ready, token } = useKeycloakReady();
  const {
    shouldFetch: policyShouldFetch,
    data: policyInfoData,
    isLoading,
  } = usePolicyInfo(SWR_KEY.POLICY_INFO, token || '', {
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
  });

  useEffect(() => {
    policyShouldFetch(ready);
  }, [ready, policyShouldFetch]);

  const onItemClick = (item: MenuInfo) => setSelectedKey(item.key);

  const currentPolicy = getCurrentPolicy(policyInfoData, selectedFilterName, selectedFilterPeriod);
  const renewalNumber = currentPolicy?.renewalNumber ?? -1;

  const {
    shouldFetch: surgeryClassifShouldFetch,
    data: surgeryClassifData,
    isLoading: surgeryClassifIsLoading,
  } = useHasSurgeryClassification(
    {
      key: SWR_KEY.HAS_SURGERY_CLASSIFICATION,
      renewalNumber,
      currentPolicy,
    },
    token || '',
    renewalNumber,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const familyRelationship = getFamilyRelationshipByCode(
    currentPolicy?.familyRelationship ?? '',
    currentPolicy?.memberType ?? '',
  );
  const { memberType, familyRelation } = familyRelationship ?? {};

  const { shouldFetch: claimTypeShouldFetch, data: claimTypeData } = useBenefitClaimType(
    { name: SWR_KEY.BENEFIT_CLAIM_TYPE, memberType, familyRelation, renewalNumber },
    token ?? '',
    memberType ?? -1,
    familyRelation ?? '',
    renewalNumber,
  );

  useEffect(() => {
    claimTypeShouldFetch(!!policyInfoData && memberType != undefined && !!familyRelation && renewalNumber !== -1);
  }, [claimTypeShouldFetch, policyInfoData, memberType, familyRelation, renewalNumber]);

  useEffect(() => {
    if (renewalNumber > 0) {
      surgeryClassifShouldFetch(ready);
    }
  }, [ready, surgeryClassifShouldFetch, renewalNumber]);

  return (
    <>
      {!surgeryClassifIsLoading && (
        <BenefitActionSection
          hasSurgeryClassification={surgeryClassifData?.hasSurgeryClassification && claimTypeData?.includes('HOSP')}
        />
      )}
      <BenefitTitle />
      <BenefitFilters
        policyInfoData={policyInfoData}
        selectedFilterName={selectedFilterName}
        selectedFilterPeriod={selectedFilterPeriod}
        setSelectedFilterName={setSelectedFilterName}
        setSelectedFilterPeriod={setSelectedFilterPeriod}
      />
      <BenefitContext.Provider
        value={{
          selectedFilterName,
          selectedFilterPeriod,
          policyInfoData,
          selectedOutpatientTab,
          setSelectedOutpatientTab,
        }}
      >
        <BenefitMenu selectedItemKey={selectedKey} onItemClick={onItemClick} />
        {selectedKey != 'OP' && (
          <BenefitNotice>
            <ImportantNotesButton />
          </BenefitNotice>
        )}

        {isLoading ? <Skeleton active={isLoading} /> : <BenefitsBody selectedKey={selectedKey} />}
      </BenefitContext.Provider>
    </>
  );
};

export default BenefitsPage;
